import { IGeoJson2DGeographicCoordinates } from '~/client/graph'
import Delivery from '~/client/src/shared/models/Delivery'
import User from '~/client/src/shared/models/User'

import MapViewItemBase from '../SitemapHelpers/models/MapViewItemBase'
import { hasDisplayedMarker } from '../SitemapHelpers/utils/markerDisplay'
import { pinCoordKey } from './pinCoordKey'
import { pinPopupOffset } from './pinPopupAnchor'
import { IWorkflowPinPoint } from './workflowPinPoints'

// The workflow icon set already carries a delivery glyph, so the mini-pin needs
// no generator of its own.
export const DELIVERY_PIN_ICON_NAME = 'delivery'

export interface IDeliveryPinGroup {
  deliveries: Delivery[]
  item: MapViewItemBase
}

export interface IDeliveryPinPointsOptions {
  shouldShowPin: (item: MapViewItemBase, deliveries: Delivery[]) => boolean
  shouldShowPinAsDone: (deliveries: Delivery[]) => boolean
  shouldShowPinAsAssigned: (deliveries: Delivery[]) => boolean
  shouldShowPinAsCanceled: (deliveries: Delivery[]) => boolean
  isPinSelected: (deliveries: Delivery[], item: MapViewItemBase) => boolean
}

// One pin per location that has deliveries; 2+ deliveries there collapse into a
// count bubble, exactly as the DOM mini-pin did.
function buildDeliveryPinPoints(
  items: MapViewItemBase[],
  deliveriesByItemId: { [itemId: string]: Delivery[] },
  options: IDeliveryPinPointsOptions,
): {
  points: IWorkflowPinPoint[]
  groupsByCoordKey: Map<string, IDeliveryPinGroup>
} {
  const points: IWorkflowPinPoint[] = []
  const groupsByCoordKey = new Map<string, IDeliveryPinGroup>()

  for (const item of items) {
    const deliveries = deliveriesByItemId[item.id]
    if (!deliveries?.length || !options.shouldShowPin(item, deliveries)) {
      continue
    }
    const coord = item.sitemapItem.coordinates
    if (coord?.latitude == null || coord?.longitude == null) {
      continue
    }

    const coordKey = pinCoordKey(coord, item.id)
    groupsByCoordKey.set(coordKey, { deliveries, item })
    points.push({
      coordKey,
      latitude: coord.latitude,
      longitude: coord.longitude,
      count: deliveries.length,
      iconName: DELIVERY_PIN_ICON_NAME,
      isSelected: options.isPinSelected(deliveries, item),
      // DeliveryStatus is a separate enum with its own colours, so these keep the older
      // boolean ladder instead of the workflow status palette.
      isDelivery: true,
      isDone: options.shouldShowPinAsDone(deliveries),
      isAssigned: options.shouldShowPinAsAssigned(deliveries),
      isCanceled: options.shouldShowPinAsCanceled(deliveries),
      // The lift only makes sense over a drawn circle.
      anchored: !!item.globeViewItemProperties?.iconProperties?.isDisplayed,
      anchorItemId: item.id,
    })
  }

  return { points, groupsByCoordKey }
}

// Glues the popup to the selected pin's tip instead of the raw location: the pin
// is lifted clear of its circle and its art reaches higher when counted, so the
// clearance mirrors what FormMapPopup gets for workflow pins.
// pinScale comes from the host: reading the continuous viewport is deliberately
// left there, so an observer only subscribes to camera frames while a popup is up.
export function getDeliveryPinPopupAnchor(
  points: IWorkflowPinPoint[],
  pinScale: number,
): { coord: IGeoJson2DGeographicCoordinates; offset: number } | null {
  const selected = points.find(p => p.isSelected)
  if (!selected) {
    return null
  }

  return {
    coord: { latitude: selected.latitude, longitude: selected.longitude },
    offset: pinPopupOffset(selected.count, selected.anchored, pinScale),
  }
}

// The four predicates DeliverySitemapPinStore exposes. Structural rather than the store type, so
// this module stays a leaf.
export interface IDeliveryPinPredicates {
  shouldShowPin: (item: MapViewItemBase, deliveries: Delivery[]) => boolean
  shouldShowPinAsDone: (deliveries: Delivery[]) => boolean
  shouldShowPinAsAssigned: (deliveries: Delivery[], user: User) => boolean
  shouldShowPinAsCanceled: (deliveries: Delivery[]) => boolean
}

// Deliveries desktop and Deliveries mobile both answer this identically: take the items that
// actually draw a marker, feed them the project's delivery map, and bind the assignment predicate to
// the signed-in user. Only the selection rule differs per surface, so that stays a parameter.
export function buildDeliveryPinData(
  displayedGlobeViewItems: MapViewItemBase[],
  deliveriesByItemId: { [itemId: string]: Delivery[] },
  predicates: IDeliveryPinPredicates,
  user: User,
  isPinSelected: (deliveries: Delivery[], item: MapViewItemBase) => boolean,
): {
  points: IWorkflowPinPoint[]
  groupsByCoordKey: Map<string, IDeliveryPinGroup>
} {
  return buildDeliveryPinPoints(
    (displayedGlobeViewItems ?? []).filter(hasDisplayedMarker),
    deliveriesByItemId,
    {
      shouldShowPin: predicates.shouldShowPin,
      shouldShowPinAsDone: predicates.shouldShowPinAsDone,
      shouldShowPinAsAssigned: deliveries =>
        predicates.shouldShowPinAsAssigned(deliveries, user),
      shouldShowPinAsCanceled: predicates.shouldShowPinAsCanceled,
      isPinSelected,
    },
  )
}
